import {Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import {OPERATOR, WhereConstraints} from '../../../graphql/common.datatypes';
import getBillingParties from './getBillingParties.api';
import Results from './Results';
import SearchFilter, {FilterValues} from './SearchFilter';
import getMyBillingParties from './getMyBillingParties.api';
import {BillingParty} from '../../../graphql/billing_party.datatypes';
import getBillingPartiesInMyGroup from './getBillingPartiesInMyGroup.api';
import {formatGraphQlDate} from '../../../utils/formatters';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    results: {
        marginTop: theme.spacing(3)
    }
}));

export const initialFilters: FilterValues = {
    email: '',
    first_name: '',
    last_name: '',
    primary_paying_memberTrue: false,
    primary_paying_memberFalse: false,
    is_group_leaderTrue: false,
    is_group_leaderFalse: false,
    hasPendingECheckPaymentsTrue: false,
    hasPendingECheckPaymentsFalse: false,
    disabledTrue: false,
    disabledFalse: true,
    has_pastdue_paymentsTrue: false,
    has_pastdue_paymentsFalse: false,
};

export const BillingPartyListContent = (props: {
    groupId?: number,
    groupRef?: string,
    bookingId?: number,
    isAdmin: boolean,
    canEdit: boolean,
    selectedBillingParty?: BillingParty,
    setSelectedBillingParty?: (party: BillingParty) => void,
    setBillingPartyFilters?: (filters: FilterValues) => void,
}) => {
    const {isAdmin, canEdit, groupRef, selectedBillingParty, setSelectedBillingParty, setBillingPartyFilters} = props;
    const classes = useStyles();
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [page, setPage] = useState(1);
    const [orderBy] = useState([{column: 'created_at', order: 'DESC'}]);
    const hasGroupBooking: WhereConstraints = { AND: [] };
    if(props.groupId){
        hasGroupBooking.AND!.push({
            column: 'GROUP_ID',
            operator: OPERATOR.EQ,
            value: props.groupId
        });
    }
    if(!isAdmin){
        const threeYears = formatGraphQlDate(moment().subtract((365*3), 'days'));
        hasGroupBooking.AND!.push({
            column: 'END_DATE',
            operator: OPERATOR.GTE,
            value: threeYears
        });
    }

    const addWhereBookingId = (constraints: WhereConstraints) => {
        const whereConstraints = {...constraints}
        if (props.bookingId) {
            whereConstraints.AND = [
                {column: 'group_booking_id', operator: OPERATOR.EQ, value: props.bookingId},
            ]
        }

        return whereConstraints;
    }

    const [filters, setFilters] = useState(initialFilters);
    const [paymentFilters, setPaymentFilters] = useState()

    const applyFilters = (newFilters: FilterValues) => {
        setFilters(newFilters);
        setWhere(getConstraintsFromFilters(newFilters));
        setHasBillingPartyMember(getPartyConstraintsFromFilters(newFilters));
        setPaymentFilters(getPaymentConstraintFromFilters(newFilters))
        setBillingPartyFilters ? setBillingPartyFilters(newFilters) : '';
    };

    const getConstraintsFromFilters = (newFilters: FilterValues) => {
        let whereConstraints: WhereConstraints = {
            AND: [],
        };

        //fields on Billing Party
        if (newFilters.id) whereConstraints.AND!.push({
            column: 'id',
            operator: OPERATOR.EQ,
            value: newFilters.id
        });
        if (newFilters.disabledTrue) whereConstraints.AND!.push({
            column: 'disabled',
            operator: OPERATOR.EQ,
            value: true
        });
        if (newFilters.disabledFalse) whereConstraints.AND!.push({
            column: 'disabled',
            operator: OPERATOR.EQ,
            value: false
        });
        if (newFilters.has_pastdue_paymentsTrue) whereConstraints.AND!.push({
            column: 'has_pastdue_payments',
            operator: OPERATOR.EQ,
            value: true
        });
        if (newFilters.has_pastdue_paymentsFalse) whereConstraints.AND!.push({
            column: 'has_pastdue_payments',
            operator: OPERATOR.EQ,
            value: false
        });

        whereConstraints = addWhereBookingId(whereConstraints)
        return whereConstraints;
    };

    const getPartyConstraintsFromFilters = (newFilters: FilterValues) => {
        const whereConstraints: WhereConstraints = {
            AND: [],
        };

        //fields on Billing Party Member
        if (newFilters.email.length > 0) whereConstraints.AND!.push({
            column: 'EMAIL',
            operator: OPERATOR.LIKE,
            value: `%${newFilters.email}%`
        });
        if (newFilters.first_name.length > 0) whereConstraints.AND!.push({
            column: 'FIRST_NAME',
            operator: OPERATOR.LIKE,
            value: `%${newFilters.first_name}%`
        });
        if (newFilters.last_name.length > 0) whereConstraints.AND!.push({
            column: 'LAST_NAME',
            operator: OPERATOR.LIKE,
            value: `%${newFilters.last_name}%`
        });
        if (newFilters.is_group_leaderTrue) whereConstraints.AND!.push({
            column: 'IS_GROUP_LEADER',
            operator: OPERATOR.EQ,
            value: true
        });
        if (newFilters.is_group_leaderFalse) whereConstraints.AND!.push({
            column: 'IS_GROUP_LEADER',
            operator: OPERATOR.EQ,
            value: false
        });

        return whereConstraints;
    };

    const getPaymentConstraintFromFilters = (newFilters: FilterValues) => {

        const pendingECheck: any[] = []

        if (newFilters.hasPendingECheckPaymentsTrue && !newFilters.hasPendingECheckPaymentsFalse) {
            pendingECheck.push({
                value1: true,
                value2: false
            })
        } else if (newFilters.hasPendingECheckPaymentsFalse && !newFilters.hasPendingECheckPaymentsTrue) {
            pendingECheck.push({
                value1: false,
                value2: true
            })
        } else {
            pendingECheck.push({
                value1: false,
                value2: false
            })
        }

        return pendingECheck
    }

    const [where, setWhere] = useState(getConstraintsFromFilters(initialFilters));
    const [hasBillingPartyMember, setHasBillingPartyMember] = useState(getPartyConstraintsFromFilters(initialFilters));
    const billingPartiesData = isAdmin ?
        getBillingParties(rowsPerPage, page, where, orderBy, hasGroupBooking, hasBillingPartyMember) :
        groupRef ?
            getBillingPartiesInMyGroup(groupRef, rowsPerPage, page, where, orderBy, hasGroupBooking, hasBillingPartyMember) :
            getMyBillingParties(rowsPerPage, page, where, orderBy, hasGroupBooking, hasBillingPartyMember);
    let billingParties
    if(billingPartiesData.data && billingPartiesData.data.billingParties) billingParties = billingPartiesData.data.billingParties;
    if(billingPartiesData.data && billingPartiesData.data.myBillingParties) billingParties = billingPartiesData.data.myBillingParties;
    if(billingPartiesData.data && billingPartiesData.data.billingPartiesInMyGroup) billingParties = billingPartiesData.data.billingPartiesInMyGroup;

    return (
      <React.Fragment>
          {isAdmin && (<SearchBar filterContent={
              <SearchFilter filters={filters} initialFilters={initialFilters} applyFilters={applyFilters}/>
          }
          />)}
          <LoadingOverlay loading={billingPartiesData.loading}>
              {billingParties && billingParties.data && (
                  <Results
                      className={classes.results}
                      billingParties={billingParties.data}
                      paginatorInfo={billingParties.paginatorInfo}
                      setPage={setPage}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={setRowsPerPage}
                      selectedBillingParty={selectedBillingParty}
                      setSelectedBillingParty={setSelectedBillingParty}
                      isAdmin={isAdmin}
                      canEdit={canEdit}
                      paymentFilters={paymentFilters}
                  />
              )}
          </LoadingOverlay>
      </React.Fragment>
  );
};

const BillingPartyList = () => {
  const classes = useStyles();
  const title = 'Billing Parties';

  return (
    <Page className={classes.root} title={title}>
        <PageHeader section="groups" title={title}/>
        <BillingPartyListContent isAdmin={true} canEdit={true}/>
    </Page>
  );
};

export default BillingPartyList;
